import { useRef, useState } from "react";
import { Barcode, RotateCcw, ScanLine } from "lucide-react";
import { StatusBadge } from "@/components/common/StatusBadge";
import type { Product, ProductIdentifierResolution } from "@/types/domain";
import {
  decideRemoteResolutionError,
  isDuplicateScan,
  normalizeScanInput,
  parsePreparedQuantity,
  productFromIdentifierResolution,
  resolveInventoryScan,
  type ScanResolution,
} from "./inventoryScan";

interface InventoryScanPanelProps {
  products: Product[];
  resolveIdentifier?: (code: string) => Promise<ProductIdentifierResolution>;
}

type ResolutionSource =
  | { kind: "remote"; typeIdentifier?: string }
  | { kind: "local_fallback" };

export function InventoryScanPanel({
  products,
  resolveIdentifier,
}: InventoryScanPanelProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const requestIdRef = useRef(0);
  const [scanValue, setScanValue] = useState("");
  const [lastScan, setLastScan] = useState<string | null>(null);
  const [resolution, setResolution] = useState<ScanResolution | null>(null);
  const [duplicateDetected, setDuplicateDetected] = useState(false);
  const [quantity, setQuantity] = useState("1");
  const [quantityAttempted, setQuantityAttempted] = useState(false);
  const [reviewReady, setReviewReady] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [resolutionSource, setResolutionSource] =
    useState<ResolutionSource | null>(null);

  const focusScanner = () => {
    window.requestAnimationFrame(() => inputRef.current?.focus());
  };

  const resetWorkflow = () => {
    requestIdRef.current += 1;
    setScanValue("");
    setLastScan(null);
    setResolution(null);
    setDuplicateDetected(false);
    setQuantity("1");
    setQuantityAttempted(false);
    setReviewReady(false);
    setResolving(false);
    setResolutionSource(null);
    focusScanner();
  };

  const submitScan = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (resolving) {
      return;
    }

    if (isDuplicateScan(scanValue, lastScan)) {
      setDuplicateDetected(true);
      setScanValue("");
      focusScanner();
      return;
    }

    const normalizedScan = normalizeScanInput(scanValue);
    const localValidation = resolveInventoryScan(scanValue, []);

    if (localValidation.kind === "empty" || localValidation.kind === "invalid") {
      setResolution(localValidation);
      setResolutionSource(null);
      setDuplicateDetected(false);
      focusScanner();
      return;
    }

    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    setLastScan(normalizedScan);
    setScanValue("");
    setDuplicateDetected(false);
    setQuantity("1");
    setQuantityAttempted(false);
    setReviewReady(false);
    setResolving(true);

    try {
      if (!resolveIdentifier) {
        setResolution(resolveInventoryScan(normalizedScan, products));
        setResolutionSource({ kind: "local_fallback" });
        return;
      }

      const remote = await resolveIdentifier(normalizedScan);
      if (requestId !== requestIdRef.current) {
        return;
      }

      setResolution({
        kind: "found",
        scan: remote.code_normalise,
        product: productFromIdentifierResolution(remote),
      });
      setResolutionSource({
        kind: "remote",
        typeIdentifier: remote.type_identifiant,
      });
    } catch (error) {
      if (requestId !== requestIdRef.current) {
        return;
      }

      const decision = decideRemoteResolutionError(error, normalizedScan);
      if (decision.kind === "fallback") {
        setResolution(resolveInventoryScan(normalizedScan, products));
        setResolutionSource({ kind: "local_fallback" });
      } else {
        setResolution(decision.resolution);
        setResolutionSource({ kind: "remote" });
      }
    } finally {
      if (requestId === requestIdRef.current) {
        setResolving(false);
        focusScanner();
      }
    }
  };

  const product =
    resolution?.kind === "found" ||
    (resolution?.kind === "inactive" && resolution.product)
      ? resolution.product
      : null;
  const preparedQuantity = parsePreparedQuantity(quantity);

  return (
    <section
      className="space-y-5 rounded-sm border border-border bg-card p-5"
      data-testid="inventory-scan-panel"
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="flex items-center gap-2">
            <Barcode className="h-5 w-5 text-primary" aria-hidden="true" />
            <h2 className="text-lg font-semibold">Scan entrepôt — aperçu</h2>
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            Résolution read-only multi-identifiants, avec fallback local explicite
            sur le SKU Rockfall exact si la nouvelle route est indisponible.
          </p>
        </div>
        <span className="inline-flex w-fit items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-800 dark:border-emerald-500/25 dark:bg-emerald-500/10 dark:text-emerald-400">
          <span className="h-2 w-2 rounded-full bg-current" />
          {resolving ? "Résolution..." : "Prêt à scanner"}
        </span>
      </div>

      <form onSubmit={submitScan} className="flex flex-col gap-3 sm:flex-row">
        <div className="flex-1">
          <label className="ops-label" htmlFor="inventory-scan-input">
            Valeur du scanner
          </label>
          <div className="relative mt-1.5">
            <ScanLine
              className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground"
              aria-hidden="true"
            />
            <input
              ref={inputRef}
              id="inventory-scan-input"
              data-testid="inventory-scan-input"
              type="text"
              autoFocus
              autoComplete="off"
              disabled={resolving}
              value={scanValue}
              onChange={(event) => setScanValue(event.target.value)}
              placeholder="Scanner puis Enter"
              className="ops-focus h-12 w-full rounded-sm border border-input bg-background pl-11 pr-3 font-mono text-base"
            />
          </div>
        </div>
        <button
          type="submit"
          disabled={resolving}
          data-testid="inventory-scan-submit"
          className="ops-focus h-12 self-end rounded-sm bg-primary px-5 text-sm font-semibold text-primary-foreground hover:bg-primary/90"
        >
          {resolving ? "Recherche..." : "Identifier"}
        </button>
        <button
          type="button"
          onClick={resetWorkflow}
          className="ops-focus inline-flex h-12 items-center justify-center gap-2 self-end rounded-sm border border-border px-4 text-sm font-medium hover:bg-muted"
        >
          <RotateCcw className="h-4 w-4" aria-hidden="true" />
          Réinitialiser
        </button>
      </form>

      <div aria-live="polite" className="space-y-3">
        {resolutionSource?.kind === "local_fallback" && (
          <div className="rounded-sm border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300">
            Route multi-identifiants indisponible : fallback local par SKU
            Rockfall exact. Aucun autre type d'identifiant ne peut être résolu.
          </div>
        )}
        {resolutionSource?.kind === "remote" &&
          resolutionSource.typeIdentifier && (
            <p className="text-sm text-muted-foreground">
              Résolution API : {resolutionSource.typeIdentifier.replace(/_/g, " ")}.
            </p>
          )}
        {duplicateDetected && (
          <div
            className="rounded-sm border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300"
            data-testid="inventory-scan-duplicate"
          >
            Double scan détecté pour <strong>{lastScan}</strong>. Aucune quantité
            n'a été ajoutée et aucune action n'a été déclenchée.
          </div>
        )}

        {resolution?.kind === "empty" && (
          <p className="text-sm text-destructive" data-testid="inventory-scan-error">
            Le scan est vide. Scannez une valeur avant d'appuyer sur Enter.
          </p>
        )}
        {resolution?.kind === "invalid" && (
          <p className="text-sm text-destructive" data-testid="inventory-scan-error">
            La valeur scannée est trop longue pour un SKU Rockfall.
          </p>
        )}
        {resolution?.kind === "not_found" && (
          <p className="text-sm text-destructive" data-testid="inventory-scan-error">
            Aucun produit ne correspond exactement au SKU « {resolution.scan} ».
          </p>
        )}
        {resolution?.kind === "ambiguous" && (
          <div
            className="rounded-sm border border-destructive/30 bg-destructive/5 p-3 text-sm"
            data-testid="inventory-scan-error"
          >
            Plusieurs produits correspondent à « {resolution.scan} ». Aucune
            sélection automatique n'est permise; signalez cette anomalie.
          </div>
        )}
        {resolution?.kind === "inactive" && (
          <p className="text-sm text-destructive" data-testid="inventory-scan-error">
            Le produit « {resolution.scan} » est inactif. Aucune préparation de
            quantité n'est permise.
          </p>
        )}
        {resolution?.kind === "error" && (
          <p className="text-sm text-destructive" data-testid="inventory-scan-error">
            {resolution.message}
          </p>
        )}
      </div>

      {product && (
        <div className="space-y-5" data-testid="inventory-scan-product">
          <div className="flex flex-wrap items-start justify-between gap-3 border-t border-border pt-5">
            <div>
              <p className="ops-label">Valeur scannée</p>
              <p className="mt-1 font-mono text-sm">{resolution && "scan" in resolution ? resolution.scan : "—"}</p>
              <h3 className="mt-3 text-xl font-semibold">
                {product.description_officielle}
              </h3>
              <p className="font-mono text-sm text-muted-foreground">{product.sku}</p>
            </div>
            <StatusBadge
              status={product.actif ? "Actif" : "Inactif"}
              tone={product.actif ? "success" : "critical"}
            />
          </div>

          <dl className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
            {[
              ["Emplacement", product.emplacement || "—"],
              ["Physique", product.quantite_physique],
              ["Réservée", product.quantite_reservee],
              ["Disponible", product.quantite_disponible],
              ["En commande", product.quantite_en_commande],
            ].map(([label, value]) => (
              <div key={label} className="rounded-sm border border-border p-3">
                <dt className="ops-label">{label}</dt>
                <dd className="mt-1 text-lg font-semibold tabular-nums">{value}</dd>
              </div>
            ))}
          </dl>

          {resolution?.kind === "found" && (
            <div className="space-y-4 border-t border-border pt-5">
              <div className="max-w-xs">
                <label className="ops-label" htmlFor="inventory-prepared-quantity">
                  Quantité proposée
                </label>
                <input
                  id="inventory-prepared-quantity"
                  data-testid="inventory-prepared-quantity"
                  type="number"
                  min="1"
                  step="1"
                  value={quantity}
                  onChange={(event) => {
                    setQuantity(event.target.value);
                    setQuantityAttempted(false);
                    setReviewReady(false);
                  }}
                  className="ops-focus mt-1.5 h-10 w-full rounded-sm border border-input bg-background px-3 text-right font-mono"
                />
                {quantityAttempted && preparedQuantity === null && (
                  <p className="mt-1.5 text-sm text-destructive">
                    Saisissez une quantité entière strictement positive.
                  </p>
                )}
              </div>

              <button
                type="button"
                data-testid="inventory-prepare-review"
                onClick={() => {
                  setQuantityAttempted(true);
                  setReviewReady(preparedQuantity !== null);
                }}
                className="ops-focus rounded-sm border border-border px-4 py-2 text-sm font-semibold hover:bg-muted"
              >
                Préparer le résumé
              </button>

              {reviewReady && preparedQuantity !== null && (
                <div
                  className="space-y-3 rounded-sm border border-blue-200 bg-blue-50 p-4 dark:border-blue-500/25 dark:bg-blue-500/10"
                  data-testid="inventory-scan-review"
                >
                  <h4 className="font-semibold">Validation visuelle seulement</h4>
                  <p className="text-sm">
                    Produit <strong>{product.sku}</strong> — quantité proposée :{" "}
                    <strong>{preparedQuantity}</strong>.
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Cette préparation reste dans le navigateur. Elle ne crée ni
                    réception, ni mouvement de stock, ni réservation.
                  </p>
                  <button
                    type="button"
                    disabled
                    data-testid="inventory-scan-confirm-disabled"
                    className="cursor-not-allowed rounded-sm bg-muted px-4 py-2 text-sm font-semibold text-muted-foreground"
                  >
                    Confirmer — désactivé
                  </button>
                </div>
              )}
            </div>
          )}

          <button
            type="button"
            onClick={resetWorkflow}
            className="ops-focus rounded-sm border border-border px-4 py-2 text-sm font-medium hover:bg-muted"
          >
            Nouveau scan
          </button>
        </div>
      )}
    </section>
  );
}
